import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import {
	deleteLocation,
	initializeLocations,
	updateLocation,
} from 'slices/locationSlice'
import { ChevronUp, ChevronDown, ArrowUpDown, List, Map, MapPin } from 'lucide-react'
import MapLocationPopupCN from 'components/popups/MapLocationPopupCN'
import LocationsListMap from './LocationsListMap'
import type { LocationMarkerData } from './LocationsListMap'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from 'components/ui/table'
import { TablePagination } from 'components/ui/pagination'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import LocationActionSheet from 'screens/locations/components/Locations/LocationActionSheet'
import { prepareLocationForSave } from 'screens/locations/utils/prepareLocationForSave'
import AltRetailerLogo from 'images/vr-new-retail_3.png'

const headerCells = [
	{ id: 'image', label: '', sortable: false, width: 'w-14' },
	{ id: 'assetName', label: 'Location Name', width: '' },
	{ id: 'address', label: 'Address', width: '' },
	{ id: 'city', label: 'City', width: '' },
	{ id: 'stateProvince', label: 'State', width: 'w-16' },
	{ id: 'postalCode', label: 'Postal Code', width: 'w-24' },
	{ id: 'isActive', label: 'Status', width: 'w-20' },
	{ id: 'isPOiOnly', label: 'POI Only', width: 'w-20' },
	{ id: 'isTenantOnly', label: 'Tenant Only', width: 'w-24' },
]

const LocationsListCN = () => {
	const dispatch = useDispatch()
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const [viewMode, setViewMode] = useState<'table' | 'map'>('table')

	const { userInfo } = useSelector((state: any) => state.userLogin)
	const userId = userInfo?._id

	const searchInputRef = useRef<HTMLInputElement>(null)
	const [searchInputValue, setSearchInputValue] = useState('')
	const [openModifyPopup, setOpenModifyPopup] = useState(false)
	const [recordForEdit, setRecordForEdit] = useState<any>(null)

	const [orderBy, setOrderBy] = useState<string | null>(null)
	const [order, setOrder] = useState<'asc' | 'desc'>('asc')
	const [page, setPage] = useState(0)
	const [rowsPerPage, setRowsPerPage] = useState(10)

	// Map popup state
	const [openMap, setOpenMap] = useState(false)
	const [mapLocation, setMapLocation] = useState<any>(null)

	const handleOpenMap = (e: React.MouseEvent, loc: any) => {
		e.stopPropagation()
		setMapLocation(loc)
		setOpenMap(true)
	}

	const {
		entities: locationEntities,
		ids: locationIds,
		isLoading: loading,
		isInitialized,
		error,
	} = useSelector((state: any) => state.locations)
	const records = locationIds.map((id: string) => locationEntities[id])

	useEffect(() => {
		// @ts-ignore
		dispatch(initializeLocations())
	}, [dispatch, isInitialized])

	const handleSort = (columnId: string) => {
		if (orderBy === columnId) {
			setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setOrderBy(columnId)
			setOrder('asc')
		}
	}

	const filteredRecords = useMemo(() => {
		if (!records || records.length === 0) return []
		if (!searchInputValue) return records
		const lowerValue = searchInputValue.toLowerCase()
		return records.filter(
			(x: any) =>
				(x.assetName && x.assetName.toLowerCase().includes(lowerValue)) ||
				(x.address && x.address.toLowerCase().includes(lowerValue)) ||
				(x.city && x.city.toLowerCase().includes(lowerValue)) ||
				(x.stateProvince && x.stateProvince.toLowerCase().includes(lowerValue)) ||
				(x.postalCode && x.postalCode.toLowerCase().includes(lowerValue)),
		)
	}, [records, searchInputValue])

	const sortedRecords = useMemo(() => {
		if (!orderBy) return filteredRecords
		return [...filteredRecords].sort((a: any, b: any) => {
			let aVal: string
			let bVal: string
			if (orderBy === 'isActive' || orderBy === 'isPOiOnly' || orderBy === 'isTenantOnly') {
				aVal = a[orderBy] ? 'yes' : 'no'
				bVal = b[orderBy] ? 'yes' : 'no'
			} else {
				aVal = (a[orderBy] || '').toString().toLowerCase()
				bVal = (b[orderBy] || '').toString().toLowerCase()
			}
			if (aVal < bVal) return order === 'asc' ? -1 : 1
			if (aVal > bVal) return order === 'asc' ? 1 : -1
			return 0
		})
	}, [filteredRecords, orderBy, order])

	const paginatedRecords = useMemo(() => {
		const start = page * rowsPerPage
		return sortedRecords.slice(start, start + rowsPerPage)
	}, [sortedRecords, page, rowsPerPage])

	// Map data: build markers with category from filtered records
	const mapData = useMemo(() => {
		const pins: LocationMarkerData[] = sortedRecords
			.filter((loc: any) => loc.latitude && loc.longitude)
			.map((loc: any) => {
				let category: LocationMarkerData['category'] = 'active'
				if (!loc.isActive) category = 'archived'
				else if (loc.locationType?.toLowerCase().includes('center') || loc.assetType === 'center') category = 'center'
				else if (loc.isPOiOnly) category = 'poi'
				else if (loc.isTenantOnly) category = 'tenant'

				return {
					id: loc._id,
					lat: loc.latitude,
					lng: loc.longitude,
					name: loc.assetName,
					address: loc.address,
					city: loc.city,
					state: loc.stateProvince,
					postalCode: loc.postalCode,
					image: loc.image,
					category,
				}
			})

		if (pins.length === 0) return { pins: [], center: [39.5, -98.35], zoom: 4 }
		if (pins.length === 1) return { pins, center: [pins[0].lat, pins[0].lng], zoom: 12 }

		const lats = pins.map(p => p.lat)
		const lngs = pins.map(p => p.lng)
		const centerLat = (Math.max(...lats) + Math.min(...lats)) / 2
		const centerLng = (Math.max(...lngs) + Math.min(...lngs)) / 2

		return { pins, center: [centerLat, centerLng], zoom: 10 }
	}, [sortedRecords])

	const SortIcon = ({ columnId }: { columnId: string }) => {
		if (orderBy !== columnId) return <ArrowUpDown className="ml-1 h-3 w-3 text-gray-300" />
		return order === 'asc' ? (
			<ChevronUp className="ml-1 h-3 w-3 text-[#0080fe]" />
		) : (
			<ChevronDown className="ml-1 h-3 w-3 text-[#0080fe]" />
		)
	}

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchInputValue(e.target.value)
		setPage(0)
	}

	const handleEditLocation = async (values: any) => {
		const newLocation = await prepareLocationForSave(values)
		const locationId = newLocation._id
		// @ts-ignore
		await dispatch(updateLocation(newLocation))
		// @ts-ignore
		dispatch(initializeLocations())
	}

	const handleDeleteLocation = async () => {
		if (!recordForEdit) return
		const locationId = recordForEdit._id
		// @ts-ignore
		await dispatch(deleteLocation({ locationId }))
		// @ts-ignore
		dispatch(initializeLocations())
	}

	const renderContent = () => {
		if (loading) {
			return (
				<div className="flex items-center justify-center py-20">
					<div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#0080fe]" />
				</div>
			)
		}

		if (error && (!records || records.length === 0)) {
			return <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-600">{error}</div>
		}

		if (!records || records.length === 0) {
			return (
				<div className="flex flex-col items-center justify-center py-20 text-gray-400">
					<svg className="h-16 w-16 mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
					</svg>
					<p className="text-sm">No locations found.</p>
				</div>
			)
		}

		return (
			<>
				<div className="max-w-[1080px] mx-auto w-full mt-4 bg-white rounded-lg border border-gray-200 p-5">
					{/* Toolbar */}
					<div className="mb-4 flex items-center justify-between gap-4">
						<div className="flex items-center gap-3">
							<input
								ref={searchInputRef}
								type="text"
								placeholder="Search by name, address, city, state, postal code..."
								value={searchInputValue}
								onChange={handleSearch}
								className="w-full max-w-sm rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 placeholder-gray-400 focus:border-[#0080fe] focus:outline-none focus:ring-1 focus:ring-[#0080fe]"
							/>
						</div>
						<div className="flex items-center gap-2">
							{/* View Toggle */}
							<div className="inline-flex items-center rounded-lg bg-gray-100 p-0.5">
								<button
									type="button"
									onClick={() => setViewMode('table')}
									className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
										viewMode === 'table'
											? 'bg-white text-[#0080fe] shadow-sm'
											: 'text-gray-500 hover:text-gray-700'
									}`}
								>
									<List className="h-3.5 w-3.5" />
									Table
								</button>
								<button
									type="button"
									onClick={() => setViewMode('map')}
									className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
										viewMode === 'map'
											? 'bg-white text-[#0080fe] shadow-sm'
											: 'text-gray-500 hover:text-gray-700'
									}`}
								>
									<Map className="h-3.5 w-3.5" />
									Map
								</button>
							</div>
						</div>
					</div>

					{/* Map View */}
					{viewMode === 'map' && (
						<div className="rounded-lg border border-gray-200 overflow-hidden">
							{mapData.pins.length > 0 ? (
								<LocationsListMap
									markers={mapData.pins}
									center={mapData.center}
									zoom={mapData.zoom}
								/>
							) : (
								<div className="flex items-center justify-center py-20 text-gray-400">
									<div className="text-center">
										<Map className="h-10 w-10 mx-auto mb-2 opacity-40" />
										<p className="text-sm">No locations with coordinates to display on the map.</p>
									</div>
								</div>
							)}
						</div>
					)}

					{/* Table View */}
					{viewMode === 'table' && (
						<>
							<div className="rounded-lg border border-gray-200 overflow-x-clip [&>div]:overflow-x-clip">
								<Table className="table-fixed w-full">
									<TableHeader>
										<TableRow className="border-b-2 border-[#0080fe] hover:bg-transparent">
											{headerCells.map(cell => (
												<TableHead
													key={cell.id}
													className={[
														cell.sortable !== false ? 'cursor-pointer select-none' : '',
														'text-gray-700 text-xs font-semibold whitespace-nowrap',
														cell.width || '',
													].join(' ')}
													onClick={cell.sortable !== false ? () => handleSort(cell.id) : undefined}
												>
													<span className="inline-flex items-center">
														{cell.label}
														{cell.sortable !== false && cell.label && <SortIcon columnId={cell.id} />}
													</span>
												</TableHead>
											))}
										</TableRow>
									</TableHeader>
									<TableBody>
										{paginatedRecords.map((location: any) => (
											<TableRow
												key={location._id}
												className="h-7 hover:bg-[#fffbf2] cursor-pointer"
												onClick={() => {
													setRecordForEdit(location)
													setOpenModifyPopup(true)
												}}
											>
												<TableCell className="py-1 text-sm">
													<Avatar className="h-8 w-8">
														<AvatarImage src={location.image || AltRetailerLogo} alt={location.assetName} />
														<AvatarFallback className="bg-gray-200 text-xs text-gray-500">
															{location.assetName?.charAt(0) || 'L'}
														</AvatarFallback>
													</Avatar>
												</TableCell>
												<TableCell className="py-1 text-sm truncate">{location.assetName}</TableCell>
												<TableCell className="py-1 text-sm truncate">
													<button
														type="button"
														onClick={e => handleOpenMap(e, location)}
														className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs text-gray-600 hover:bg-[#0080fe]/10 hover:text-[#0080fe] hover:border-[#0080fe]/20 transition-colors cursor-pointer"
													>
														<MapPin className="h-3 w-3" />
														{location.address}
													</button>
												</TableCell>
												<TableCell className="py-1 text-sm truncate">{location.city}</TableCell>
												<TableCell className="py-1 text-sm">{location.stateProvince}</TableCell>
												<TableCell className="py-1 text-sm">{location.postalCode}</TableCell>
												<TableCell className="py-1 text-sm">
													<span className={location.isActive ? 'text-green-600' : 'text-gray-400'}>
														{location.isActive ? 'Active' : 'Inactive'}
													</span>
												</TableCell>
												<TableCell className="py-1 text-sm">{location.isPOiOnly ? 'Yes' : 'No'}</TableCell>
												<TableCell className="py-1 text-sm">{location.isTenantOnly ? 'Yes' : 'No'}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>

							{sortedRecords.length > 4 && (
								<TablePagination
									page={page}
									rowsPerPage={rowsPerPage}
									count={sortedRecords.length}
									onPageChange={setPage}
									onRowsPerPageChange={setRowsPerPage}
								/>
							)}
						</>
					)}
				</div>

				<LocationActionSheet
					location={recordForEdit}
					open={openModifyPopup}
					onClose={() => setOpenModifyPopup(false)}
					onEditLocation={handleEditLocation}
					onDelete={handleDeleteLocation}
				/>

				{openMap && mapLocation && (
					<MapLocationPopupCN
						open={openMap}
						onClose={() => setOpenMap(false)}
						location={mapLocation}
					/>
				)}
			</>
		)
	}

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<div className="flex-1 flex overflow-hidden">
					<main className="flex-1 overflow-y-auto p-6">
						<h2 className="text-2xl font-semibold text-gray-800 mb-4">Locations</h2>
						{renderContent()}
					</main>
				</div>
			</div>
		</div>
	)
}

export default LocationsListCN
